/**
 * Notifications Dropdown Component
 *
 * A bell icon in the header that shows unread notification count
 * and opens a dropdown panel with notification list.
 * Supports:
 * - Bell icon with unread badge
 * - Dropdown panel with notifications list
 * - Click to navigate to related request
 * - Mark as read on click
 * - Mark all as read button
 * - Dark theme with glass morphism styling
 */

import { renderIcon } from '../utils/icons';
import {
  subscribeToInAppNotifications,
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  startPolling,
  stopPolling,
  selectNotifications,
  selectUnreadCount,
  selectIsLoading,
  type InAppNotification,
  type InAppNotificationsState,
} from '../services/in-app-notifications';

/**
 * Notification type to icon mapping
 */
const NOTIFICATION_ICONS: Record<string, { icon: string; color: string }> = {
  REQUEST_SUBMITTED: { icon: 'send', color: 'primary' },
  REQUEST_APPROVED: { icon: 'checkCircle', color: 'success' },
  REQUEST_REJECTED: { icon: 'xCircle', color: 'destructive' },
  REQUEST_CANCELLED: { icon: 'xCircle', color: 'muted-foreground' },
  APPROVAL_REQUIRED: { icon: 'clock', color: 'warning' },
  COMMENT_ADDED: { icon: 'fileText', color: 'info' },
  DELEGATION_ASSIGNED: { icon: 'users', color: 'primary' },
  DELEGATION_ENDED: { icon: 'users', color: 'muted-foreground' },
};

/**
 * Options for the notifications dropdown component
 */
export interface NotificationsDropdownOptions {
  /** Custom class name for the container */
  className?: string;
  /** Callback when a notification is clicked */
  onNotificationClick?: (notification: InAppNotification) => void;
  /** Maximum notifications to show in dropdown (default: 10) */
  maxVisible?: number;
  /** Whether to start polling automatically (default: true) */
  autoStartPolling?: boolean;
}

/**
 * Format relative time for notification timestamps
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CH', { month: 'short', day: 'numeric' });
}


/**
 * Create a single notification item element
 */
function createNotificationItem(
  notification: InAppNotification,
  onClick: (notification: InAppNotification) => void
): HTMLElement {
  const item = document.createElement('div');
  item.className = `
    notification-item flex items-start gap-3 px-4 py-3
    hover:bg-muted/50 transition-colors cursor-pointer
    ${notification.is_read ? 'opacity-60' : ''}
  `.replace(/\s+/g, ' ').trim();
  item.setAttribute('role', 'menuitem');
  item.setAttribute('tabindex', '0');
  item.dataset.notificationId = notification.id;

  const iconConfig = NOTIFICATION_ICONS[notification.type] || { icon: 'bell', color: 'primary' };

  // Icon container
  const iconContainer = document.createElement('div');
  iconContainer.className = `
    w-8 h-8 rounded-full bg-${iconConfig.color}/20
    flex items-center justify-center shrink-0 mt-0.5
  `.replace(/\s+/g, ' ').trim();
  iconContainer.appendChild(
    renderIcon(iconConfig.icon as Parameters<typeof renderIcon>[0], {
      size: 'sm',
      className: `text-${iconConfig.color}`,
    })
  );

  // Content container
  const content = document.createElement('div');
  content.className = 'flex-1 min-w-0';

  // Title
  const title = document.createElement('p');
  title.className = `text-sm font-medium text-foreground truncate ${notification.is_read ? '' : 'font-semibold'}`;
  title.textContent = notification.title;
  content.appendChild(title);

  // Message
  const message = document.createElement('p');
  message.className = 'text-xs text-muted-foreground line-clamp-2';
  message.textContent = notification.message;
  content.appendChild(message);

  // Timestamp
  const timestamp = document.createElement('p');
  timestamp.className = 'text-xs text-muted-foreground mt-1';
  timestamp.textContent = formatRelativeTime(notification.created_at);
  content.appendChild(timestamp);

  // Unread indicator
  if (!notification.is_read) {
    const unreadDot = document.createElement('div');
    unreadDot.className = 'w-2 h-2 rounded-full bg-primary shrink-0 mt-2';
    item.appendChild(unreadDot);
  }

  item.appendChild(iconContainer);
  item.appendChild(content);

  // Handle click
  const handleClick = () => onClick(notification);
  item.addEventListener('click', handleClick);
  item.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  });

  return item;
}

/**
 * Create loading skeleton
 */
function createLoadingSkeleton(): HTMLElement {
  const skeleton = document.createElement('div');
  skeleton.className = 'space-y-2 p-4';

  for (let i = 0; i < 3; i++) {
    const item = document.createElement('div');
    item.className = 'flex items-start gap-3 animate-pulse';
    item.innerHTML = `
      <div class="w-8 h-8 rounded-full bg-muted shrink-0"></div>
      <div class="flex-1 space-y-2">
        <div class="h-4 bg-muted rounded w-3/4"></div>
        <div class="h-3 bg-muted rounded w-1/2"></div>
      </div>
    `;
    skeleton.appendChild(item);
  }

  return skeleton;
}

/**
 * Create empty state
 */
function createEmptyState(): HTMLElement {
  const empty = document.createElement('div');
  empty.className = 'py-12 text-center';
  empty.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
      ${renderIcon('bell', { size: 'lg', className: 'text-muted-foreground' }).outerHTML}
    </div>
    <p class="text-sm font-medium text-foreground mb-1">No notifications</p>
    <p class="text-xs text-muted-foreground">You're all caught up!</p>
  `;
  return empty;
}

/**
 * Create the notifications dropdown component
 */
export function createNotificationsDropdown(
  options: NotificationsDropdownOptions = {}
): HTMLElement {
  const {
    className = '',
    onNotificationClick,
    maxVisible = 10,
    autoStartPolling = true,
  } = options;

  // Container
  const container = document.createElement('div');
  container.className = `notifications-dropdown relative ${className}`.trim();

  // Bell button (trigger)
  const bellButton = document.createElement('button');
  bellButton.type = 'button';
  bellButton.className = `
    relative p-2 rounded-lg text-muted-foreground
    hover:text-foreground hover:bg-muted
    transition-colors focus:outline-none focus:ring-2
    focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
  `.replace(/\s+/g, ' ').trim();
  bellButton.setAttribute('aria-label', 'Notifications');
  bellButton.setAttribute('aria-haspopup', 'true');
  bellButton.setAttribute('aria-expanded', 'false');

  // Bell icon
  const bellIcon = renderIcon('bell', { size: 'md' });
  bellButton.appendChild(bellIcon);

  // Badge for unread count
  const badge = document.createElement('span');
  badge.className = `
    notification-badge absolute -top-1 -right-1
    min-w-[18px] h-[18px] px-1
    flex items-center justify-center
    text-[10px] font-bold text-primary-foreground
    bg-destructive rounded-full
    transform scale-0 transition-transform duration-200
  `.replace(/\s+/g, ' ').trim();
  badge.setAttribute('aria-hidden', 'true');
  bellButton.appendChild(badge);

  container.appendChild(bellButton);

  // Dropdown panel
  const dropdown = document.createElement('div');
  dropdown.className = `
    notifications-panel absolute right-0 top-full mt-2
    w-80 sm:w-96 max-h-[70vh]
    bg-card border border-border rounded-xl shadow-xl
    hidden z-50 overflow-hidden
    animate-scale-in origin-top-right
  `.replace(/\s+/g, ' ').trim();
  dropdown.setAttribute('role', 'menu');
  dropdown.setAttribute('aria-label', 'Notifications');

  // Header
  const header = document.createElement('div');
  header.className = 'flex items-center justify-between px-4 py-3 border-b border-border';
  header.innerHTML = `
    <div class="flex items-center gap-2">
      ${renderIcon('bell', { size: 'sm', className: 'text-primary' }).outerHTML}
      <h3 class="text-sm font-semibold text-foreground">Notifications</h3>
    </div>
  `;

  // Mark all as read button
  const markAllBtn = document.createElement('button');
  markAllBtn.type = 'button';
  markAllBtn.className = `
    mark-all-btn text-xs text-primary hover:text-primary/80
    transition-colors hidden
  `.replace(/\s+/g, ' ').trim();
  markAllBtn.textContent = 'Mark all read';
  markAllBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    try {
      markAllBtn.disabled = true;
      markAllBtn.textContent = 'Marking...';
      await markAllAsRead();
      markAllBtn.textContent = 'Mark all read';
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      markAllBtn.textContent = 'Mark all read';
    } finally {
      markAllBtn.disabled = false;
    }
  });
  header.appendChild(markAllBtn);
  dropdown.appendChild(header);

  // Notifications list container
  const listContainer = document.createElement('div');
  listContainer.className = 'notifications-list max-h-[50vh] overflow-y-auto';
  dropdown.appendChild(listContainer);

  container.appendChild(dropdown);

  // State
  let isOpen = false;

  // Open/close dropdown
  function openDropdown(): void {
    isOpen = true;
    dropdown.classList.remove('hidden');
    bellButton.setAttribute('aria-expanded', 'true');
    // Refresh notifications when opening
    fetchNotifications().catch(console.error);
  }

  function closeDropdown(): void {
    isOpen = false;
    dropdown.classList.add('hidden');
    bellButton.setAttribute('aria-expanded', 'false');
  }

  function toggleDropdown(): void {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }

  // Bell button click handler
  bellButton.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleDropdown();
  });

  // Close when clicking outside
  document.addEventListener('click', (e) => {
    if (isOpen && !container.contains(e.target as Node)) {
      closeDropdown();
    }
  });

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (isOpen && e.key === 'Escape') {
      closeDropdown();
      bellButton.focus();
    }
  });

  // Handle notification click
  const handleNotificationClick = async (notification: InAppNotification): Promise<void> => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Close dropdown
    closeDropdown();

    // Call custom handler if provided
    if (onNotificationClick) {
      onNotificationClick(notification);
    } else if (notification.request_id) {
      // Default: navigate to request
      window.history.pushState({}, '', `/requests/${notification.request_id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  // Update UI based on state
  function updateUI(state: InAppNotificationsState): void {
    const notifications = selectNotifications(state) || [];
    const unreadCount = selectUnreadCount(state) || 0;
    const isLoading = selectIsLoading(state) || false;

    // Update badge
    if (unreadCount > 0) {
      badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
      badge.style.transform = 'scale(1)';
      bellButton.setAttribute('aria-label', `Notifications (${unreadCount} unread)`);
    } else {
      badge.style.transform = 'scale(0)';
      bellButton.setAttribute('aria-label', 'Notifications');
    }

    // Update mark all button visibility
    if (unreadCount > 0) {
      markAllBtn.classList.remove('hidden');
    } else {
      markAllBtn.classList.add('hidden');
    }

    // Update list content
    listContainer.innerHTML = '';

    if (isLoading && notifications.length === 0) {
      listContainer.appendChild(createLoadingSkeleton());
      return;
    }

    if (notifications.length === 0) {
      listContainer.appendChild(createEmptyState());
      return;
    }

    // Render notification items
    const visibleNotifications = notifications.slice(0, maxVisible);
    visibleNotifications.forEach((notification) => {
      const item = createNotificationItem(notification, handleNotificationClick);
      listContainer.appendChild(item);
    });

    // Show "more" indicator if there are more notifications
    if (notifications.length > maxVisible) {
      const moreIndicator = document.createElement('div');
      moreIndicator.className = 'px-4 py-2 text-center text-xs text-muted-foreground border-t border-border';
      moreIndicator.textContent = `+${notifications.length - maxVisible} more notifications`;
      listContainer.appendChild(moreIndicator);
    }
  }

  // Subscribe to state changes
  const unsubscribe = subscribeToInAppNotifications(updateUI);

  // Start polling if enabled
  if (autoStartPolling) {
    startPolling();
  }

  // Cleanup function
  (container as HTMLElement & { cleanup?: () => void }).cleanup = () => {
    unsubscribe();
    stopPolling();
  };

  return container;
}

/**
 * Cleanup notifications dropdown component
 */
export function cleanupNotificationsDropdown(element: HTMLElement): void {
  const cleanup = (element as HTMLElement & { cleanup?: () => void }).cleanup;
  if (cleanup) {
    cleanup();
  }
}

/**
 * NotificationsDropdown class for more control
 */
export class NotificationsDropdown {
  private container: HTMLElement;
  private unsubscribe: () => void;

  constructor(options: NotificationsDropdownOptions = {}) {
    this.container = createNotificationsDropdown(options);
    this.unsubscribe = () => {
      cleanupNotificationsDropdown(this.container);
    };
  }

  /**
   * Get the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Mount to a parent element
   */
  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  /**
   * Unmount and cleanup
   */
  destroy(): void {
    this.unsubscribe();
    this.container.remove();
  }

  /**
   * Refresh notifications
   */
  async refresh(): Promise<void> {
    await fetchNotifications();
  }
}
